<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import OT from "@opentok/client";

import { VIDEO_CALL_STATUS, type Device, type deviceKind } from "./definitions";
import { VIDEO_CALL_COMPLETE_REASON } from "@/Modules/Models/Api/VideoCalls";
import { type Option } from "@/Components/UI/TcSelect.vue";
import { config as APP_CONFIG } from "@/Common/envConfig";

import { TcButton, Container, Column, TcLoader } from "@/Components/UI";
import TcIcon from "@/Components/Extend/TcIcon.vue";
import ConfigCallForm from "./components/ConfigCallForm.vue";

import useVideoCallStore from "../Store";
import useUserStore from "@/Modules/User/Store";
import useChatStore from "@/Modules/Chat/Store";

import { isValidString } from "@/Utils/String";
import { hasValue } from "@/Utils/Objects";
import { danger, log } from "@/Utils/Debug";
import { isIosApp } from "@/Utils/Ionic";

import { isBackCamera, getDevices } from "./utils";

import callSound from "./assets/call.mp3";

const MAX_LOOP_CALL_SOUND = 2;

type configType = {
  videoId?: Option;
  audioId?: Option;
  outputId?: Option;
  blur?: boolean;
  mirror?: boolean;
  video: boolean;
  audio: boolean;
};

const { t } = useI18n();

const videoCallStore = useVideoCallStore();
const userStore = useUserStore();
const chatStore = useChatStore();

const hasCreationError = ref<boolean>(false);
const audioRef = ref<HTMLAudioElement | null>(null);
const callAudioLoops = ref<number>(0);
const devices = ref<Device[]>([]);
const isLoading = ref<boolean>(false);
const isLoadingVideo = ref<boolean>(false);
const isLoadingDevices = ref<boolean>(false);
const videoRef = ref<HTMLVideoElement>();
const config = reactive<configType>({
  videoId: undefined,
  audioId: undefined,
  outputId: undefined,
  blur: undefined,
  mirror: undefined,
  video: true,
  audio: true,
});

let publisher: OT.Publisher | undefined = undefined;

const isValidConfig = computed((): boolean => {
  if (isValidString(config.audioId?.value)) {
    return true;
  }

  return false;
});

const publisherConfig = computed(
  (): OT.PublisherProperties => ({
    insertMode: "append",
    height: "100%",
    width: "100%",
    mirror: config.mirror === true ? config.mirror : false,
    videoSource: config.videoId?.value as string,
    videoFilter: config.blur ? { blurStrength: "high", type: "backgroundBlur" } : undefined,
    audioSource: false,
    frameRate: 15,
    insertDefaultUI: false,
  })
);

const sessionDuration = computed((): number => {
  if (hasValue(chatStore.getActiveRoom) && hasValue(chatStore.getActiveRoom.sessionDuration)) {
    return chatStore.getActiveRoom.sessionDuration;
  }
  return APP_CONFIG.defaultMinSessionDuration;
});

const deviceToOptions = (device: Device): Option => {
  return {
    label: device.label,
    value: device.deviceId,
  };
};

const updatePublisher = () => {
  if (hasValue(publisher)) {
    publisher.destroy();
  }

  publisher = OT.initPublisher(undefined, publisherConfig.value, (err?: OT.OTError) => {
    if (hasValue(err)) {
      danger("Init publisher", err);
    }
  });

  publisher.on("videoElementCreated", handleVideoElementCreated);
};

const updateVideoStream = async (streamId: string) => {
  if (!videoRef.value) return;

  isLoadingVideo.value = true;

  log("Update video stream", streamId, hasValue(publisher));

  if (!hasValue(publisher)) {
    updatePublisher();
  } else {
    log("UPDATE SOURCE");

    try {
      await publisher.clearVideoFilter();
      await publisher.setVideoSource(streamId);
      await publisher.applyVideoFilter({ type: "backgroundBlur", blurStrength: "high" });
    } catch (e) {
      log("Error setting video source", e);

      updatePublisher();
    }
  }

  isLoadingVideo.value = false;
};

const updateVideo = (value: Option) => {
  if (!hasValue(value)) return;

  if (isValidString(value.value)) {
    config.videoId = value;
    isLoadingVideo.value = true;

    if (isBackCamera(value.label)) {
      handleChangeMirror(false);
    } else {
      handleChangeMirror(true);
    }

    updateVideoStream(value.value);
  }
};

const updateAudio = (value: Option) => {
  if (!hasValue(value)) return;

  if (isValidString(value.value)) {
    config.audioId = value;
  }
};

const updateOutput = (value: Option) => {
  if (!hasValue(value)) return;

  if (isValidString(value.value)) {
    config.outputId = value;
  }
};

const handleChangeBlur = (value: boolean) => {
  config.blur = value;

  if (hasValue(publisher)) {
    if (value) {
      publisher.applyVideoFilter({ type: "backgroundBlur", blurStrength: "high" });
    } else {
      publisher.clearVideoFilter();
    }
  }
};

const handleChangeMirror = (value: boolean) => {
  config.mirror = value;
};

const handleToogleVideo = () => {
  config.video = !config.video;
};

const handleToogleAudio = () => {
  config.audio = !config.audio;
};

const handleVideoElementCreated = (
  event: OT.Event<"videoElementCreated", OT.Publisher> & { element: HTMLVideoElement | HTMLObjectElement }
) => {
  if (hasValue(event.element)) {
    if (hasValue(videoRef.value)) {
      const videos = videoRef.value.getElementsByTagName("video");

      if (videos.length > 0) {
        for (let i = 0; i < videos.length; i++) {
          videoRef.value.removeChild(videos[i]!);
        }
      }
    }

    videoRef.value!.appendChild(event.element);
  }

  isLoadingVideo.value = false;
};

const handleCloseConfig = () => {
  if (userStore.getUserIsTherapist) {
    videoCallStore.updateShowConfig(false);
  } else {
    videoCallStore.updateShowConfig(false);
    videoCallStore.updateStatus(VIDEO_CALL_STATUS.CLOSING);
    videoCallStore.hangDownVideoCall(
      isValidString(videoCallStore.roomId) ? videoCallStore.roomId : videoCallStore.nextCallOptions?.roomId,
      VIDEO_CALL_COMPLETE_REASON.REJECTED
    );
  }
};

const handleSaveConfig = async () => {
  if (!isValidConfig.value || isLoading.value) return;

  isLoading.value = true;
  hasCreationError.value = false;

  videoCallStore.saveConfig({
    videoId: config.videoId?.value as string | undefined,
    audioId: config.audioId?.value as string,
    outputId: config.outputId?.value as string | undefined,
    blur: hasValue(config.blur) && config.blur ? "high" : false,
    mirror: config.mirror as boolean,
    video: config.video,
    audio: config.audio,
  });

  try {
    if (userStore.getUserIsTherapist) {
      const isCreated = await videoCallStore.startTherapistCall(chatStore.activeChatRoomId!, false);

      isLoading.value = false;

      if (!isCreated) {
        hasCreationError.value = true;
      }
    } else {
      videoCallStore.setupVideoCall(videoCallStore.nextCallOptions!);
    }
  } catch (e) {
    danger("Error setupVideoCall", e);

    isLoading.value = false;
  }
};

/* Audio */

const playAudio = () => {
  if (userStore.getUserIsTherapist) return;

  callAudioLoops.value = 0;

  if (hasValue(audioRef.value)) {
    pauseAudio();

    audioRef.value.muted = false;
    audioRef.value.play();

    audioRef.value.onended = () => {
      if (callAudioLoops.value < MAX_LOOP_CALL_SOUND) {
        callAudioLoops.value++;

        if (hasValue(audioRef.value)) {
          audioRef.value!.play();
        }
      }
    };
  }
};

const pauseAudio = () => {
  if (userStore.getUserIsTherapist) return;

  if (hasValue(audioRef.value)) {
    audioRef.value.muted = true;

    if (!audioRef.value.paused) {
      audioRef.value.pause();
    }
  }
};

/* Audio */

/* UTILS */

const checkDeviceOption = (
  deviceId: string | boolean | undefined,
  kind: deviceKind,
  devices: Device[]
): Option | undefined => {
  if (isValidString(deviceId)) {
    const currentDevice = devices.findIndex((d) => d.kind === kind && deviceId === d.deviceId);

    if (currentDevice >= 0) {
      return deviceToOptions(devices[currentDevice]!);
    }
  }

  const defaultDevice = devices.find((d) => d.kind === kind);

  if (hasValue(defaultDevice)) {
    return deviceToOptions(defaultDevice);
  }

  return undefined;
};

/* UTILS */

const handleUpdateDevices = async () => {
  isLoadingDevices.value = true;

  devices.value = await getDevices(t);

  isLoadingDevices.value = false;
};

onBeforeMount(async () => {
  devices.value = await getDevices(t);
  isLoadingVideo.value = true;
  isLoading.value = false;

  const callConfig = videoCallStore.getConfig();

  config.blur = OT.hasMediaProcessorSupport() ? !!callConfig?.blur : false;
  config.video = callConfig?.video ?? true;
  config.audio = callConfig?.audio ?? true;
  config.mirror = callConfig?.mirror ?? false;
  config.videoId = checkDeviceOption(callConfig?.videoId, "videoInput", devices.value);
  config.audioId = checkDeviceOption(callConfig?.audioId, "audioInput", devices.value);
  config.outputId = checkDeviceOption(callConfig?.outputId, "audioOutput", devices.value);

  if (hasValue(config.videoId)) {
    updateVideoStream(config.videoId.value as string);
  } else {
    updatePublisher();
  }
});

onMounted(() => {
  playAudio();
});

onBeforeUnmount(() => {
  pauseAudio();

  if (hasValue(publisher)) {
    publisher.destroy();
  }
});
</script>

<template>
  <div class="video_config">
    <div class="video_config__content" :class="{ 'video_config__content--ios': isIosApp() }">
      <div class="video_config__content__header">
        <h1 class="typography-high-l color-content-high" :class="{ 'mb-2': hasCreationError }">
          <template v-if="userStore.getUserIsTherapist">
            {{ t("general.videocall.config.title", { name: chatStore.getUserActiveRoom?.username }) }}
          </template>

          <template v-else>
            {{ t("general.videocall.config.title.patient", { name: chatStore.getUserActiveRoom?.username }) }}
          </template>
        </h1>

        <div
          v-if="hasCreationError"
          class="shape-radius-s bg-semantic-error-low p-4 shadow-l border-b-2-solid bc-semantic-error-important"
          style="display: flex; align-items: center"
        >
          <TcIcon name="WarningCircle" color="semantic-error" />
          <p class="typography-low-l color-content-high ml-5">
            {{ t("general.videocall.config.error.connection") }}
          </p>
        </div>
      </div>

      <Container columns="auto 1fr" area="subtitle" align="center" :gap="2">
        <Column>
          <TcIcon name="InfoCircle" color="content-high" />
        </Column>

        <Column>
          <p>{{ t("general.videocall.config.session_duration", { minutes: sessionDuration }) }}</p>
        </Column>
      </Container>

      <div
        ref="videoRef"
        class="video_config__content__video bg-background-overlay"
        :class="{
          'video_config__content__video--mirror': config.mirror,
          'video_config__content__video--disabled': !config.video,
        }"
      >
        <div class="video_config__content__video__actions">
          <TcIcon
            class="video_config__content__video__actions__icon"
            :class="{ 'video_config__content__video__actions__icon--disabled': !config.video }"
            :name="config.video ? 'CamActive' : 'CamInactive'"
            size="lg"
            color="background-alt"
            @click="handleToogleVideo"
          />

          <TcIcon
            class="video_config__content__video__actions__icon"
            :class="{ 'video_config__content__video__actions__icon--disabled': !config.audio }"
            :name="config.audio ? 'VoiceMessage' : 'VoiceMessageActive'"
            size="lg"
            color="background-alt"
            @click="handleToogleAudio"
          />
        </div>

        <TcLoader v-if="isLoadingVideo" class="video_config__content__video__loader" color="background-alt" />
      </div>

      <ConfigCallForm
        :config="config"
        :devices="devices"
        :isLoadingDevices="isLoadingDevices"
        @updateVideo="updateVideo"
        @updateAudio="updateAudio"
        @updateOutput="updateOutput"
        @updateMirror="handleChangeMirror"
        @updateBlur="handleChangeBlur"
        @updateDevices="handleUpdateDevices"
      />

      <div class="video_config__content__actions">
        <TcButton
          color="danger"
          isFitContent
          prefixIcon="RejectedCall"
          prefixIconSize="lg"
          :disabled="isLoading || videoCallStore.isLoading"
          :isBlock="false"
          @click="handleCloseConfig"
        >
          <template v-if="userStore.getUserIsTherapist">
            {{ t("general.videocall.config.button.cancel") }}
          </template>

          <template v-else>
            {{ t("general.videocall.config.button.reject") }}
          </template>
        </TcButton>

        <TcButton
          class="video_config__content__config__actions__save"
          isFitContent
          color="success"
          sufixIcon="HangCall"
          sufixIconSize="lg"
          :isLoading="isLoading || videoCallStore.isLoading"
          :disabled="!isValidConfig"
          :isBlock="false"
          @click="handleSaveConfig"
        >
          {{ t("general.videocall.config.button.save") }}
        </TcButton>
      </div>
    </div>

    <div class="video_config__overlay" color="primary-low-solid" />

    <audio ref="audioRef" v-if="userStore.getUserIsPatient" :src="callSound" muted>
      <source :src="callSound" />
    </audio>
  </div>
</template>

<style lang="scss">
.video_config {
  @include sp.padding(false, 4);

  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ($breakpoint-m)) {
    align-items: flex-start;
  }

  &__overlay {
    @include colors.bg(background, overlay);

    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;

    @media (max-width: ($breakpoint-m)) {
      display: none;
    }
  }

  &__content {
    @include sp.padding(false, 4);
    @include colors.border(content, low-solid, true);
    @include colors.bg("background", base);
    @include radius(l);

    width: 100%;
    max-width: $size-modal-max-width-lg;
    height: fit-content;
    display: grid;
    grid-template-areas: "header header" "subtitle subtitle" "video config" "actions actions";
    grid-template-columns: 1fr 1fr;
    grid-column-gap: $space-4;
    grid-row-gap: $space-4;
    border: 1px solid;

    @media (max-width: ($breakpoint-m)) {
      @include sp.padding(false, 6);

      border: none;
      grid-template-areas: "header" "video" "config" "actions";
      grid-template-rows: auto auto 1fr auto;
      grid-template-columns: 1fr;
      overflow-y: visible;
      max-height: calc(100vh - $space-8);
    }

    &--ios {
      max-height: calc(100vh - $space-8 - $space-10);
    }

    &__header {
      grid-area: header;
    }

    &__video {
      @include radius(m);

      min-height: 150px;
      grid-area: video;
      position: relative;
      overflow: hidden;

      @media (max-width: ($breakpoint-m)) {
        max-height: none;
        width: 100%;
        max-height: 250px;

        video {
          height: 100%;
        }
      }

      video {
        width: 100%;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      &__actions {
        position: absolute;
        bottom: $space-6;
        z-index: 1;
        width: 100%;
        display: flex;
        justify-content: center;
        gap: $space-4;

        &__icon {
          @include sp.padding(false, 2);
          @include colors.bg(content, high);

          box-sizing: content-box;
          height: $space-6;
          width: $space-6;
          border-radius: 100%;
          cursor: pointer;

          &--disabled {
            @include colors.bg(content, mid-solid);
          }
        }
      }

      &__loader {
        z-index: 1;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      &--mirror {
        transform: scaleX(-1);

        .video_config__content__video__actions {
          transform: scaleX(-1);
        }
      }

      &--disabled {
        video {
          opacity: 0;
        }
      }
    }

    &__config {
      @include utils.noScrollbar();

      & {
        grid-area: config;
        overflow-y: visible;
      }
    }

    &__actions {
      grid-area: actions;
      display: flex;
      gap: $space-4;
      justify-content: flex-end;

      @media (max-width: ($breakpoint-m)) {
        justify-content: space-between;
      }

      @media (max-width: $breakpoint-xs) {
        @include sp.padding(b, 4);

        flex-direction: column-reverse;

        button {
          width: 100%;
        }
      }
    }
  }
}
</style>
